'use client';

import { useEffect, useState } from 'react';

import { apiClient } from '@/lib/api/client';

import {
  emptyAdSettingsFlags,
  normalizeAdSettingsFlags,
  type AdSettingsFlags,
} from './placements';

type AdSettingsResponse = {
  data?: Partial<Record<string, string>>;
};

/** Match API cache so admin flips are visible without a full hard refresh forever. */
export const AD_SETTINGS_CLIENT_CACHE_TTL_MS = 60_000;

let cachedFlags: AdSettingsFlags | null = null;
let cachedAt = 0;
let inflight: Promise<AdSettingsFlags> | null = null;

function cacheIsFresh(): boolean {
  return cachedFlags !== null && Date.now() - cachedAt < AD_SETTINGS_CLIENT_CACHE_TTL_MS;
}

/** Fetches and caches /pro/ad-settings. Failures fail closed (all no) and are not cached. */
export async function loadAdSettings(): Promise<AdSettingsFlags> {
  if (cacheIsFresh() && cachedFlags) {
    return cachedFlags;
  }
  if (inflight) {
    return inflight;
  }
  inflight = (async () => {
    try {
      const { data } = await apiClient.get<AdSettingsResponse>('/pro/ad-settings');
      const flags = normalizeAdSettingsFlags(data && data.data);
      cachedFlags = flags;
      cachedAt = Date.now();
      return flags;
    } catch {
      return emptyAdSettingsFlags();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Shared client cache of /pro/ad-settings. Failures fail closed (all no). */
export function useAdSettings(): {
  flags: AdSettingsFlags;
  loaded: boolean;
} {
  const [flags, setFlags] = useState<AdSettingsFlags>(
    () => (cacheIsFresh() && cachedFlags ? cachedFlags : emptyAdSettingsFlags()),
  );
  const [loaded, setLoaded] = useState(() => cacheIsFresh());

  useEffect(() => {
    let cancelled = false;
    void loadAdSettings().then((next) => {
      if (!cancelled) {
        setFlags(next);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { flags, loaded };
}

/** Test helper: clear the module cache. */
export function __resetAdSettingsCacheForTests(): void {
  cachedFlags = null;
  cachedAt = 0;
  inflight = null;
}
